import React, { useState, useEffect } from 'react';

const countryNames = {
  ID: 'Indonesia', SG: 'Singapore', MY: 'Malaysia', TH: 'Thailand',
  PH: 'Philippines', VN: 'Vietnam', AU: 'Australia', GB: 'United Kingdom',
  CY: 'Cyprus', SC: 'Seychelles', MU: 'Mauritius', BZ: 'Belize',
  US: 'United States', JP: 'Japan', HK: 'Hong Kong', AE: 'UAE',
};

const StarRating = ({ rating }) => {
  const r = parseFloat(rating) || 0;
  return (
    <span>
      {[1,2,3,4,5].map(i => (
        <span key={i} style={{ color: i <= Math.round(r) ? '#fbbf24' : '#334155', fontSize: 14 }}></span>
      ))}
      <span style={{ fontSize: 12, color: 'var(--text-muted)', marginLeft: 4 }}>{r.toFixed(1)}</span>
    </span>
  );
};

const BrokerCard = ({ broker, recommended }) => {
  const initials = broker.name.split(' ').map(w => w[0]).join('').slice(0,2).toUpperCase();
  const instruments = broker.trading_instruments
    ? broker.trading_instruments.split(',').map(s => s.trim()).filter(Boolean)
    : [];

  return (
    <div style={{
      background: 'var(--bg-surface)',
      border: `1px solid ${recommended ? 'var(--accent)' : 'var(--border)'}`,
      borderRadius: 'var(--radius-xl)',
      padding: 24,
      position: 'relative',
      transition: 'all var(--t)',
      boxShadow: recommended ? '0 0 0 1px var(--accent), var(--shadow-md)' : 'var(--shadow-sm)',
    }}
    onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = 'var(--shadow-md)'; }}
    onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = recommended ? '0 0 0 1px var(--accent), var(--shadow-md)' : 'var(--shadow-sm)'; }}
    >
      {recommended && (
        <div style={{
          position: 'absolute', top: -1, right: 20,
          background: 'var(--accent)', color: '#fff',
          fontSize: 10, fontWeight: 700, padding: '3px 10px',
          borderRadius: '0 0 var(--radius-md) var(--radius-md)',
          letterSpacing: '0.06em', textTransform: 'uppercase',
        }}>Recommended</div>
      )}

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, marginBottom: 16 }}>
        <div style={{
          width: 52, height: 52, borderRadius: 'var(--radius-md)',
          background: broker.logo_url ? '#fff' : 'var(--accent-soft)',
          border: '1px solid var(--border)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0, overflow: 'hidden',
        }}>
          {broker.logo_url
            ? <img src={broker.logo_url} alt={broker.name} style={{ width: '100%', height: '100%', objectFit: 'contain', padding: 4 }} />
            : <span style={{ fontSize: 16, fontWeight: 800, color: 'var(--accent)' }}>{initials}</span>
          }
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-main)', marginBottom: 2 }}>{broker.name}</div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
            {broker.headquarters || countryNames[broker.hq_country_code] || ''}
            {broker.year_founded > 0 && <span style={{ color: 'var(--text-faint)' }}> · Est. {broker.year_founded}</span>}
          </div>
          <StarRating rating={broker.rating} />
        </div>
      </div>

      {/* Stats grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 16 }}>
        {[
          { label: 'Min Deposit', value: broker.min_deposit > 0 ? `$${broker.min_deposit}` : 'None' },
          { label: 'Leverage', value: broker.leverage || '1:500' },
          { label: 'Regulation', value: broker.regulation || 'N/A' },
        ].map(({ label, value }) => (
          <div key={label} style={{
            background: 'var(--bg-elevated)', borderRadius: 'var(--radius-md)',
            padding: '8px 10px', textAlign: 'center',
          }}>
            <div style={{ fontSize: 10, color: 'var(--text-faint)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>{label}</div>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-main)' }}>{value}</div>
          </div>
        ))}
      </div>

      {/* Description */}
      {broker.description && (
        <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 14, lineHeight: 1.5 }}>
          {broker.description}
        </div>
      )}

      {/* Instruments */}
      {instruments.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 16 }}>
          {instruments.map(ins => (
            <span key={ins} style={{
              padding: '3px 8px', borderRadius: 'var(--radius-full)',
              background: 'var(--bg-elevated)', border: '1px solid var(--border)',
              fontSize: 11, color: 'var(--text-muted)', fontWeight: 500,
            }}>{ins}</span>
          ))}
        </div>
      )}

      {/* Platforms */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 18 }}>
        {(broker.supported_platforms || ['MT4','MT5']).map(p => (
          <span key={p} style={{
            padding: '3px 10px', borderRadius: 'var(--radius-full)',
            background: 'var(--success-soft)', border: '1px solid rgba(134,239,172,0.2)',
            fontSize: 11, color: 'var(--success)', fontWeight: 600,
          }}>{p}</span>
        ))}
      </div>

      {/* CTA */}
      <a
        href={broker.referral_link || broker.website_url}
        target="_blank"
        rel="noreferrer"
        style={{
          display: 'block', width: '100%', padding: '11px 0',
          background: 'var(--accent)', borderRadius: 'var(--radius-md)',
          color: '#fff', textAlign: 'center', fontWeight: 700, fontSize: 14,
          textDecoration: 'none', transition: 'all var(--t)', boxSizing: 'border-box',
        }}
        onMouseEnter={e => { e.currentTarget.style.background = 'var(--accent-hover)'; }}
        onMouseLeave={e => { e.currentTarget.style.background = 'var(--accent)'; }}
      >
        Open Account
      </a>
      <div style={{ textAlign: 'center', fontSize: 11, color: 'var(--text-faint)', marginTop: 8 }}>
        Via CrunchAlpha IB link - Your funds stay at the broker
      </div>
    </div>
  );
};

const BrokerMarketplace = () => {
  const [brokers, setBrokers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userCountry, setUserCountry] = useState('');
  const [filter, setFilter] = useState('all'); // all | recommended | local

  useEffect(() => {
    // Detect user country via IP
    fetch('https://ipapi.co/json/')
      .then(r => r.json())
      .then(d => setUserCountry(d.country_code || ''))
      .catch(() => {});

    // Fetch brokers
    fetch('/api/public/brokers')
      .then(r => r.json())
      .then(d => setBrokers(d.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const activeBrokers = brokers.filter(b => b.is_active);

  const sorted = [...activeBrokers].sort((a, b) => {
    // Local brokers first if country matches
    const aLocal = userCountry && a.hq_country_code === userCountry;
    const bLocal = userCountry && b.hq_country_code === userCountry;
    if (aLocal && !bLocal) return -1;
    if (!aLocal && bLocal) return 1;
    if (a.is_recommended && !b.is_recommended) return -1;
    if (!a.is_recommended && b.is_recommended) return 1;
    return (a.display_order || 0) - (b.display_order || 0);
  });

  const filtered = filter === 'recommended'
    ? sorted.filter(b => b.is_recommended)
    : filter === 'local'
    ? sorted.filter(b => b.hq_country_code === userCountry)
    : sorted;

  const localCount = activeBrokers.filter(b => b.hq_country_code === userCountry).length;

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', paddingBottom: 60 }}>
      {/* Header — clean, functional */}
      <div style={{
        background: 'var(--bg-surface)',
        borderBottom: '1px solid var(--border)',
        padding: '20px 24px',
      }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 14 }}>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--text-main)', margin: '0 0 4px' }}>
              Partner Brokers
            </h1>
            <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: 0, lineHeight: 1.6 }}>
              Regulated brokers · Instant copy trading activation · Your capital stays at the broker
            </p>
          </div>
          <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
            {[['','Regulated'],['','MT4 & MT5'],['','IB rebate, no extra cost']].map(([ic,lb])=>(
              <div key={lb} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text-faint)' }}>
                <span>{ic}</span><span>{lb}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '24px 24px 0' }}>
        {/* Filter bar */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 28, flexWrap: 'wrap', gap: 12 }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-main)' }}>
              {filtered.length} Partner Broker{filtered.length !== 1 ? 's' : ''}
            </div>
            {userCountry && localCount > 0 && (
              <div style={{ fontSize: 12, color: 'var(--accent)', marginTop: 2 }}>
                 {localCount} broker{localCount > 1 ? 's' : ''} available in your country
              </div>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {[
              { key: 'all', label: 'All Brokers' },
              { key: 'recommended', label: '⭐ Recommended' },
              ...(userCountry && localCount > 0 ? [{ key: 'local', label: ` ${countryNames[userCountry] || userCountry}` }] : []),
            ].map(({ key, label }) => (
              <button key={key} onClick={() => setFilter(key)} style={{
                padding: '7px 16px', borderRadius: 'var(--radius-full)',
                border: `1px solid ${filter === key ? 'var(--accent)' : 'var(--border)'}`,
                background: filter === key ? 'var(--accent-soft)' : 'transparent',
                color: filter === key ? 'var(--accent)' : 'var(--text-muted)',
                fontSize: 13, fontWeight: 600, cursor: 'pointer', transition: 'all var(--t)',
              }}>{label}</button>
            ))}
          </div>
        </div>

        {/* Notice */}
        <div style={{
          background: 'var(--accent-soft)', border: '1px solid var(--accent-border)',
          borderRadius: 'var(--radius-md)', padding: '12px 16px',
          fontSize: 13, color: 'var(--accent)', marginBottom: 28,
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <span>ℹ  </span>
          <span>After opening an account, return to CrunchAlpha and connect it via <strong>Add Account → Connect Existing</strong>. Your funds always remain at the broker.</span>
        </div>

        {/* Grid */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: 80, color: 'var(--text-muted)' }}>Loading brokers...</div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 80, color: 'var(--text-muted)' }}>No brokers found.</div>
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
            gap: 20,
          }}>
            {filtered.map(b => (
              <BrokerCard
                key={b.id}
                broker={b}
                recommended={b.is_recommended && (userCountry ? b.hq_country_code === userCountry || !localCount : true)}
              />
            ))}
          </div>
        )}

        {/* Back link */}
        <div style={{ textAlign: 'center', marginTop: 40 }}>
          <button onClick={()=>window.history.back()}
            style={{ fontSize: 13, color: 'var(--text-muted)', background:'none', border:'none', cursor:'pointer' }}>
            ← Back
          </button>
        </div>
      </div>
    </div>
  );
};

export default BrokerMarketplace;
